import React, { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Map, { Marker } from "react-map-gl";
import * as turf from "@turf/turf";
import { Spin, Radio, Space } from "antd";

import { SubmitIcon } from "components/Icons";
import { PrimaryButton, SecondaryButton, TextButton } from "components/Buttons";
import markerIcons from "components/Markers/markerIcons";

import { doUpdateLocation } from "modules/location/stores/thunk";
import { doDetailZone } from "modules/zone/stores/thunk";

import { MAP_CONFIG, MAP_STYLES } from "../Main";

import "./ConfirmAddress.scss";

const viewport = {
  width: "100%",
  height: "100%",
  zoom: 10,
};

const ConfirmAddress = ({
  zone,
  location,
  circle,
  isFetching,
  onClosePopup,
}) => {
  const dispatch = useDispatch();
  const mapRef = useRef();
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(0);
  const {
    _id: locationId,
    lat: locationLat,
    formattedAddress: locationFormattedAddress,
    lng: locationLng,
    type: locationType,
    color: locationColor,
    orderNo,
    alternativeLocations,
  } = location || {};

  const locationOptions = useMemo(
    () => [
      {
        lat: locationLat,
        lng: locationLng,
        formattedAddress: locationFormattedAddress,
      },
      ...alternativeLocations,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alternativeLocations]
  );

  const handleChangeAltLocation = (e) => {
    const selectedIdx = e.target.value;
    const selectedLocation = locationOptions[selectedIdx];
    setSelectedLocationIdx(selectedIdx);
    if (selectedLocation?.lat) {
      mapRef.current.flyTo({
        center: [selectedLocation.lng, selectedLocation.lat],
        duration: 2000,
      });
    }
  };

  const handleConfirm = async (updateAll = false) => {
    try {
      const selectedLocation = locationOptions[selectedLocationIdx];
      if (!!selectedLocation) {
        const { lat, lng, formattedAddress } = selectedLocation;
        const point = turf.point([lng, lat]);
        const polygon = turf.polygon([...circle.geometry.coordinates]);
        const payload = {
          id: locationId,
          lat,
          lng,
          updatedInZone: turf.booleanPointInPolygon(point, polygon),
        };
        if (updateAll) payload.address = formattedAddress;
        await dispatch(doUpdateLocation(payload));
        await dispatch(doDetailZone(zone._id));
      }
    } finally {
      onClosePopup();
    }
  };

  const markerCoord = useMemo(() => {
    const selectedLocation = locationOptions[selectedLocationIdx];
    return {
      lat: selectedLocation?.lat || locationLat,
      lng: selectedLocation?.lng || locationLng,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationIdx, locationLat, locationLng]);

  return (
    <Spin spinning={isFetching}>
      <div className="confirm-address">
        <div className="main-content">
          <div className="left-content">
            <h5>Confirm Address</h5>
            <label style={{ marginBottom: 12, display: "block" }}>
              Select address
            </label>
            <Radio.Group
              onChange={handleChangeAltLocation}
              value={selectedLocationIdx}
            >
              <Space direction="vertical">
                {locationOptions?.map(({ formattedAddress }, idx) => (
                  <Radio key={`alt-location-option-${idx}`} value={idx}>
                    {formattedAddress}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>
          <Map
            style={{ height: 360, width: "50%", borderRadius: 8 }}
            ref={mapRef}
            initialViewState={{
              latitude: locationLat,
              longitude: locationLng,
              ...viewport,
            }}
            mapStyle={MAP_STYLES.standard}
            {...MAP_CONFIG}
          >
            {markerCoord?.lat && (
              <Marker
                latitude={markerCoord.lat}
                longitude={markerCoord.lng}
                style={{ zIndex: 9 }}
              >
                <div className="marker">
                  <img src={markerIcons[locationType][locationColor]} alt="" />
                  <div
                    className="marker-info"
                    style={{
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    {orderNo}
                  </div>
                </div>
              </Marker>
            )}
            {locationOptions.map((altLocation, idx) => {
              if (
                !altLocation.lat ||
                (altLocation.lat === markerCoord.lat &&
                  altLocation.lng === markerCoord.lng)
              )
                return null;
              return (
                <Marker
                  key={`alt-location-${idx}`}
                  latitude={altLocation.lat}
                  longitude={altLocation.lng}
                  style={{ zIndex: 9 }}
                >
                  <div className="marker">
                    <img
                      src={markerIcons["alternativeLocation"]["888888"]}
                      alt=""
                    />
                  </div>
                </Marker>
              );
            })}
          </Map>
        </div>
        <div className="bottom-buttons">
          <TextButton label="Avbryt" onClick={onClosePopup} />
          <SecondaryButton
            type="primary"
            label="Update Coordinates"
            icon={<SubmitIcon />}
            onClick={handleConfirm}
          />
          <PrimaryButton
            size="lg"
            type="primary"
            label="Update Address"
            icon={<SubmitIcon />}
            onClick={() => handleConfirm(true)}
          />
        </div>
      </div>
    </Spin>
  );
};

export default ConfirmAddress;
